import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Action } from '../models/action.model';
import { Kyc_Check } from '../models/kyc_check.model';
import { notesAllowProceed, isAgentSkipCheck } from '../utils/kyc-postpone.util';
import {
  InternalIssuesResolutionResult,
  InternalValidations,
  KYCActionState,
  ValidationResolver,
  ValidationResolverContext,
  ValidationResolutionResult,
} from '../models/kyc-validation.model';
import { KycService } from './kyc.service';
import { UserService } from './user.service';

/**
 * Service centralizado para gerenciar estados e validações de KYC
 * 
 * Responsabilidades:
 * - Verificar estado atual de um action de KYC
 * - Gerenciar resolvers de validações internas
 * - Coordenar tentativas de resolver problemas internos
 * - NÃO gerencia UI (modais, templates, etc) - isso fica com os componentes
 */
@Injectable({
  providedIn: 'root',
})
export class KycStateService {
  /**
   * Registry de resolvers
   * Cada componente pode registrar seus próprios resolvers
   */
  private resolvers: ValidationResolver[] = [];

  constructor(private kycService: KycService, private userService: UserService) {}

  /**
   * Registra um resolver para um tipo específico de validação
   * Permite que componentes adicionem suas próprias estratégias de resolução
   */
  registerResolver(resolver: ValidationResolver): void {
    console.log('[KYC State] Registering resolver for:', resolver.validationType);
    this.resolvers.push(resolver);
  }

  /**
   * Remove todos os resolvers registrados
   * Útil para cleanup ou testes
   */
  clearResolvers(): void {
    this.resolvers = [];
  }

  /**
   * Busca um resolver apropriado para uma validação
   */
  private findResolver(context: ValidationResolverContext): ValidationResolver | null {
    return (
      this.resolvers.find((resolver) => resolver.canResolve(context)) || null
    );
  }

  /**
   * Verifica o estado atual de um KYC action
   * 
   * Estados possíveis:
   * - LOADING: Verificação inicial
   * - BLOCKED: Action anterior não completado
   * - PENDING: Aguardando início do usuário
   * - IN_PROGRESS: KYC sendo processado
   * - DONE_OK: KYC completado com sucesso
   * - DONE_FAIL: KYC falhou (pode ter validações internas para resolver)
   */
  async checkState(
    action: Action,
    kycCheck?: Kyc_Check
  ): Promise<KYCActionState> {
    console.log('[KYC State] Checking state for action:', action.action_id);
    console.log('[KYC State] ac_status:', action.ac_status, 'prstatus:', action.prstatus);

    // Se KYC foi completado, verifica resultado independente de prstatus
    if (action.ac_status === 'DONE' || action.ac_status === 'RESUBMISSION') {
      if (!action.done_action_id) {
        console.error(
          '[KYC State] Action is DONE but missing done_action_id',
          action
        );
        return 'PENDING';
      }

      // Busca o KYC check (usa cache se fornecido)
      const check = kycCheck || await this.getKycCheck(action.done_action_id);

      if (!check) {
        console.error(`[KYC State] KYC check not found: ${action.done_action_id}`);
        return 'PENDING';
      }

      // Check if KYC was bypassed by an agent
      const currentUser = await this.userService.getCurrentUser();
      const bypassedByAgent = !!(currentUser?.attributes as any)?.kyc?.bypassedByAgent;

      console.log('[KYC State] KYC check passed:', check.passed, 'bypassedByAgent:', bypassedByAgent);

      // CPK-3620: a FAILED check whose notes allow proceeding (postpone) lets
      // the client advance — treated like a pass for gating purposes.
      const canProceed = notesAllowProceed((check as any).notes);

      // CPK-4051: an agent-driven skip (SKIPPED_BY_AGENT + IC_AGENT_FLOW) also
      // grants passage — the backend signature gate already accepts these rows.
      const agentSkip = isAgentSkipCheck(check as any);

      // CPK-4012: cpv_actions_done attributes a KYC check to EVERY owner claim of the
      // user (matched by provider), so `done_action_id` here may be a check that belongs
      // to a DIFFERENT claim. A non-passed (failed/postponed) check must only gate the
      // claim it belongs to — otherwise a claim-A reject leaks onto claim B and disables
      // "Proceed". Passed checks are reused across claims (AC#2) and are exempt. Legacy
      // checks with no claim_id are left as-is (no binding) to avoid a prod regression.
      const checkClaimId = (check as any).claim_id;
      if (!check.passed && !bypassedByAgent && !agentSkip && checkClaimId && action.claim_id && checkClaimId !== action.claim_id) {
        console.log('[KYC State] non-passed KYC check belongs to another claim — treating as PENDING', {
          actionId: action.action_id,
          kycCheckId: action.done_action_id,
          checkClaimId,
          actionClaimId: action.claim_id,
        });
        return 'PENDING';
      }

      // Verifica resultado - DONE_OK se passed=true, bypassed por agente,
      // postponed, ou skipped por agente (CPK-4051)
      if (check.passed || bypassedByAgent || canProceed || agentSkip) {
        console.log('[KYC State] ✅ Returning DONE_OK', {
          actionId: action.action_id,
          kycCheckId: action.done_action_id,
          passed: check.passed,
          bypassedByAgent,
          canProceed,
          agentSkip,
          stage: check.stage,
        });
        return 'DONE_OK';
      }

      console.log('[KYC State] ❌ Returning DONE_FAIL (passed = false or null)', {
        actionId: action.action_id,
        kycCheckId: action.done_action_id,
        passed: check.passed,
        bypassedByAgent,
        stage: check.stage,
      });
      return 'DONE_FAIL';
    }

    // Verifica se está em processamento
    if (action.ac_status === 'PROCESSING') {
      return 'IN_PROGRESS';
    }

    // Verifica se ação anterior está completa (apenas se KYC ainda não foi iniciado)
    if (
      action.prstatus !== 'DONE' &&
      action.prstatus !== 'SUBMITTED' &&
      action.prstatus !== null
    ) {
      return 'BLOCKED';
    }

    // Verifica se KYC ainda não foi iniciado
    if (!action.ac_status || action.ac_status === 'ERROR') {
      return 'PENDING';
    }

    // Fallback para PENDING se nenhuma condição foi satisfeita
    return 'PENDING';
  }

  /**
   * Busca um KYC check pelo ID
   */
  async getKycCheck(kycCheckId: string): Promise<Kyc_Check | null> {
    try {
      return await this.kycService.getById(kycCheckId);
    } catch (error) {
      console.error('[KYC State] Error fetching KYC check:', error);
      return null;
    }
  }

  /**
   * Verifica se há validações internas no KYC check
   */
  hasInternalValidations(kycCheck: Kyc_Check): boolean {
    const validations = this.getInternalValidations(kycCheck);
    return validations !== null;
  }

  /**
   * Extrai validações internas do provider_return
   */
  getInternalValidations(kycCheck: Kyc_Check): InternalValidations | null {
    return kycCheck.provider_return?.internalValidations || null;
  }

  /**
   * Verifica se todas as validações internas passaram
   */
  allInternalValidationsPassed(kycCheck: Kyc_Check): boolean {
    const validations = this.getInternalValidations(kycCheck);
    if (!validations) return true; // Se não tem validações, considera como passou
    return validations.allPassed;
  }

  /**
   * Verifica se o provider suporta validações internas
   * Atualmente Veriff, VeriffLite e o clone SomosKycV1
   */
  providerSupportsInternalValidations(provider: string): boolean {
    return provider === 'veriff' || provider === 'verifflite' || provider === 'somoskyc_v1' || provider === 'document_digital';
  }

  /**
   * Tenta resolver validações internas que falharam
   * 
   * Fluxo:
   * 1. Verifica se há validações internas
   * 2. Para cada validação que falhou, busca um resolver apropriado
   * 3. Tenta resolver usando o resolver encontrado
   * 4. Revalida o KYC check após resoluções
   * 5. Retorna resultado agregado
   * 
   * O componente que chama decide o que fazer com o resultado:
   * - Se allResolved === true e newState === 'DONE_OK': action completado
   * - Se requiresNewKyc === true: precisa refazer KYC completo
   * - Se someResolved === true: alguns resolvidos, outros não
   */
  async checkAndResolveInternalIssues(
    action: Action,
    resolverContext?: { dialog?: MatDialog; [key: string]: any }
  ): Promise<InternalIssuesResolutionResult> {
    console.log('[KYC State] Checking internal issues for action:', action.action_id);

    // Validação básica
    if (!action.done_action_id) {
      return {
        hadIssues: false,
        allResolved: false,
        someResolved: false,
        requiresNewKyc: false,
        unresolvedValidations: [],
      };
    }

    // Buscar KYC check
    const kycCheck = await this.getKycCheck(action.done_action_id);
    if (!kycCheck) {
      console.error('[KYC State] KYC check not found');
      return {
        hadIssues: false,
        allResolved: false,
        someResolved: false,
        requiresNewKyc: true,
        unresolvedValidations: [],
      };
    }

    // Verificar se provider suporta validações internas
    if (!this.providerSupportsInternalValidations(kycCheck.provider || '')) {
      console.log('[KYC State] Provider does not support internal validations');
      return {
        hadIssues: false,
        allResolved: false,
        someResolved: false,
        requiresNewKyc: false,
        unresolvedValidations: [],
      };
    }

    // Verificar validações internas
    const internalValidations = this.getInternalValidations(kycCheck);
    if (!internalValidations || internalValidations.allPassed) {
      console.log('[KYC State] No internal validation issues');
      return {
        hadIssues: false,
        allResolved: true,
        someResolved: false,
        requiresNewKyc: false,
        unresolvedValidations: [],
      };
    }

    console.log('[KYC State] Found internal validation issues:', internalValidations);

    // Filtrar validações que falharam
    const failedValidations = internalValidations.results.filter(
      (result) => !result.passed
    );

    console.log('[KYC State] Failed validations:', failedValidations.length);

    // Tentar resolver cada validação
    const resolutionResults: ValidationResolutionResult[] = [];
    const unresolvedValidations: string[] = [];
    let requiresNewKyc = false;

    for (const validation of failedValidations) {
      console.log('[KYC State] Attempting to resolve:', validation.validation);

      const context: ValidationResolverContext = {
        validation,
        kycCheck,
        action,
        ...resolverContext, // Passa contexto adicional (ex: dialog)
      };

      // Buscar resolver apropriado
      const resolver = this.findResolver(context);

      if (!resolver) {
        console.warn(
          '[KYC State] No resolver found for:',
          validation.validation
        );
        unresolvedValidations.push(validation.validation);
        requiresNewKyc = true; // Se não tem resolver, provavelmente precisa refazer KYC
        continue;
      }

      // Tentar resolver
      try {
        const result = await resolver.resolve(context);
        resolutionResults.push(result);

        if (!result.resolved) {
          unresolvedValidations.push(validation.validation);
        }

        if (result.requiresNewKyc) {
          requiresNewKyc = true;
        }
      } catch (error) {
        console.error('[KYC State] Error resolving validation:', error);
        unresolvedValidations.push(validation.validation);
        requiresNewKyc = true;
      }
    }

    // Calcular resultado agregado
    const allResolved = unresolvedValidations.length === 0;
    const someResolved = resolutionResults.some((r) => r.resolved);

    // Se todas foram resolvidas, revalidar o KYC check
    let newState: KYCActionState | undefined;
    if (allResolved) {
      console.log('[KYC State] All validations resolved, revalidating KYC check');
      const revalidationResult = await this.revalidateKycCheck(kycCheck.id!);
      if (revalidationResult) {
        newState = await this.checkState(action, revalidationResult.data);
      }
    }

    return {
      hadIssues: true,
      allResolved,
      someResolved,
      requiresNewKyc,
      newState,
      unresolvedValidations,
    };
  }

  /**
   * Revalida um KYC check após correções
   * Chama a lambda que executa novamente as validações internas
   */
  async revalidateKycCheck(kycCheckId: string): Promise<{
    message: string;
    data: Kyc_Check;
    validationSummary: {
      allPassed: boolean;
      passedCount: number;
      failedCount: number;
      notesAdded: number;
      notesRemoved: number;
    };
  } | null> {
    try {
      console.log('[KYC State] Revalidating KYC check:', kycCheckId);
      return await this.kycService.revalidateKycCheck(kycCheckId);
    } catch (error) {
      console.error('[KYC State] Error revalidating KYC check:', error);
      return null;
    }
  }
}
